// src/components/ProjectManagement/ProjectPlanner.jsx
import React, { useState, useEffect, useRef } from 'react';
import { db } from '../../firebase/config';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, arrayUnion, arrayRemove } from 'firebase/firestore';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import ClientView from './ClientView';

// ========== NEW: ARTISANS VIEW COMPONENT ==========
const ArtisansView = ({ plannerData, clientsList }) => {
  // Helper to get client name from clientId
  const getClientName = (clientId) => {
    if (!clientId) return null;
    const client = clientsList.find(c => c.id === clientId);
    return client ? client.client : null;
  };

  // Helper to render a list of items (with client names)
  const renderItems = (items) => {
    if (!items || items.length === 0) {
      return <div style={{ color: '#94a3b8', fontStyle: 'italic', fontSize: '14px' }}>No tasks</div>;
    }
    return items.map((item, idx) => {
      const text = typeof item === 'object' ? item.text : item;
      const clientId = typeof item === 'object' ? item.clientId : null;
      const clientName = clientId ? getClientName(clientId) : null;
      return (
        <div
          key={idx}
          style={{
            padding: '8px 12px',
            marginBottom: '6px',
            backgroundColor: '#ffffff',
            borderRadius: '8px',
            border: '1px solid #e2e8f0',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            fontSize: '14px',
          }}
        >
          <span>{text}</span>
          {clientName && (
            <span style={{ color: '#64748b', fontSize: '12px', fontStyle: 'italic' }}>
              {clientName}
            </span>
          )}
        </div>
      );
    });
  };

  return (
    <div style={{ padding: '24px 0' }}>
      <h2 style={{ fontSize: '24px', fontWeight: '600', color: '#1e293b', marginBottom: '24px' }}>
        👷 Artisans Overview
      </h2>
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
        gap: '24px',
      }}>
        {plannerData.map((artisan) => (
          <div
            key={artisan.id}
            style={{
              backgroundColor: 'white',
              borderRadius: '16px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
              border: '1px solid #e2e8f0',
              overflow: 'hidden',
              transition: 'transform 0.1s ease, box-shadow 0.2s ease',
              display: 'flex',
              flexDirection: 'column',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 8px 20px rgba(0,0,0,0.08)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.05)';
            }}
          >
            {/* Card Header: Artisan Name */}
            <div style={{
              padding: '16px 20px',
              backgroundColor: '#800020',
              color: 'white',
              fontSize: '18px',
              fontWeight: '600',
              letterSpacing: '0.3px',
            }}>
              {artisan.carpenter || 'Unnamed Artisan'}
            </div>

            {/* Card Body */}
            <div style={{ padding: '16px 20px 20px 20px', flex: 1 }}>
              {/* Today's Work */}
              <div style={{ marginBottom: '16px' }}>
                <h4 style={{ fontSize: '14px', fontWeight: '600', color: 'green', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  ⚙️ Today’s Work
                </h4>
                {renderItems(artisan.currentWork)}
              </div>

              {/* Next Planned */}
              <div>
                <h4 style={{ fontSize: '14px', fontWeight: '600', color: 'black', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  📋 Next Planned
                </h4>
                {renderItems(artisan.nextPlanned)}
              </div>
            </div>
          </div>
        ))}
      </div>
      {plannerData.length === 0 && (
        <div style={{ textAlign: 'center', padding: '60px 20px', color: '#94a3b8' }}>
          No artisans available.
        </div>
      )}
    </div>
  );
};

// ========== MAIN PROJECT PLANNER COMPONENT ==========
const ProjectPlanner = ({ currentUser, userRole }) => {
  // ========== STATE ==========
  const [plannerData, setPlannerData] = useState([]);
  const [users, setUsers] = useState([]);
  const [clientsList, setClientsList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addingItem, setAddingItem] = useState(null);
  const [newItemText, setNewItemText] = useState('');
  const [newItemClientId, setNewItemClientId] = useState('');
  const [newItemStartDate, setNewItemStartDate] = useState('');
  const [newItemEndDate, setNewItemEndDate] = useState('');
  const [newInchargeName, setNewInchargeName] = useState('');
  const [showAddRowModal, setShowAddRowModal] = useState(false);
  const [showInchargeSearchModal, setShowInchargeSearchModal] = useState(false);
  const [currentInchargeRowId, setCurrentInchargeRowId] = useState(null);
  const [newCarpenterName, setNewCarpenterName] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [employeeSearchTerm, setEmployeeSearchTerm] = useState('');
  const [inchargeSearchTerm, setInchargeSearchTerm] = useState('');
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [filteredInchargeEmployees, setFilteredInchargeEmployees] = useState([]);
  const [editingCarpenter, setEditingCarpenter] = useState(null);
  const [editCarpenterValue, setEditCarpenterValue] = useState('');
  const [showShiftModal, setShowShiftModal] = useState(false);
  const [shiftSource, setShiftSource] = useState({ rowId: null, field: null, item: null });
  const [shiftTargetField, setShiftTargetField] = useState('');
  const [dragOverField, setDragOverField] = useState(null);
  const [dragOverRowId, setDragOverRowId] = useState(null);
  const [dragOverItemIndex, setDragOverItemIndex] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [editItemValue, setEditItemValue] = useState('');
  const [activeTab, setActiveTab] = useState('planner');

  // ========== REFS ==========
  const isProcessingDrop = useRef(false);
  const isAutoMoving = useRef(false);

  // ========== COLUMN COLORS ==========
  const columnColors = {
    rowNumber:   { bg: '#F8FAFC', border: '#E2E8F0' },
    carpenter:   { bg: '#FFF5F5', border: '#FED7D7' },
    currentWork: { bg: '#FFFAF0', border: '#FDE68A' },
    nextPlanned: { bg: '#F0FDF4', border: '#A7F3D0' },
    preparation: { bg: '#FEFCE8', border: '#FDE047' },
    incharge:    { bg: '#EFF6FF', border: '#BFDBFE' },
    pipeLine:    { bg: '#FAF5FF', border: '#D8B4FE' },
    actions:     { bg: '#FEF2F2', border: '#FECACA' },
  };

  const columnOrder = ['currentWork', 'nextPlanned', 'preparation', 'pipeLine'];
  const columnLabels = {
    currentWork: '⚙️ TODAY’S WORK',
    nextPlanned: '📋 NEXT PLANNED',
    preparation: '📦 PREPARATION',
    pipeLine:    '🔧 PIPE LINE',
  };

  // ========== PDF EXPORT ==========
  const exportToPDF = () => {
    const doc = new jsPDF({ orientation: 'landscape', unit: 'mm' });
    doc.setFontSize(18);
    doc.setTextColor(128, 0, 32);
    doc.text('Project Planner Report', 14, 20);
    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100);
    doc.text(`Generated: ${new Date().toLocaleString()}`, 14, 30);

    const getDaysText = (startDate, endDate) => {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (startDate) {
        const start = new Date(startDate + 'T00:00:00');
        const diffTime = start - today;
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        if (diffDays > 0) {
          return `(Starts in ${diffDays}d)`;
        }
      }
      if (!endDate) return '';
      const end = new Date(endDate + 'T00:00:00');
      const diffTime = end - today;
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      if (diffDays < 0) {
        return `(Overdue by ${-diffDays}d)`;
      } else if (diffDays === 0) {
        return '(Today)';
      } else {
        return `(${diffDays} d left)`;
      }
    };

    const headers = ['#', 'ARTISAN', 'TODAYS WORK', 'NEXT PLANNED'];
    const rows = plannerData.map((row, index) => {
      const formatArray = (arr) => {
        if (!arr || arr.length === 0) return '—';
        return arr.map(item => {
          let text = typeof item === 'object' ? item.text : item;
          let clientName = '';
          let startDate = '';
          let endDate = '';
          if (typeof item === 'object') {
            if (item.clientId) {
              const client = clientsList.find(c => c.id === item.clientId);
              if (client) clientName = client.client;
            }
            startDate = item.startDate || '';
            endDate = item.endDate || '';
          }
          let fullText = text;
          if (clientName) fullText += ` (${clientName})`;
          const daysTag = getDaysText(startDate, endDate);
          if (daysTag) fullText += ` ${daysTag}`;
          return fullText;
        }).join('\n\n');
      };
      return [
        String(index + 1),
        row.carpenter || '—',
        formatArray(row.currentWork),
        formatArray(row.nextPlanned),
      ];
    });

    autoTable(doc, {
      head: [headers],
      body: rows,
      startY: 40,
      theme: 'plain',
      headStyles: { fillColor: [128, 0, 32], textColor: [255, 255, 255], fontSize: 11, fontStyle: 'bold', halign: 'left', valign: 'middle', lineWidth: 0.1, lineColor: [255, 255, 255] },
      bodyStyles: { fontSize: 9, cellPadding: 4, valign: 'top', lineWidth: 0.1, lineColor: [226, 232, 240] },
      columnStyles: {
        0: { fillColor: [248, 250, 252] },
        1: { fillColor: [255, 245, 245] },
        2: { fillColor: [255, 250, 240] },
        3: { fillColor: [240, 253, 244] },
      },
    });
    doc.save(`project-planner-${new Date().toISOString().split('T')[0]}.pdf`);
  };

  // ========== FETCH ==========
  useEffect(() => { fetchUsers(); }, []);
  useEffect(() => { fetchClients(); }, []);
  useEffect(() => { fetchPlannerData(); }, []);

  useEffect(() => {
    if (!employeeSearchTerm.trim()) { setFilteredEmployees([]); return; }
    const term = employeeSearchTerm.toLowerCase();
    setFilteredEmployees(
      users.filter(u => u.name?.toLowerCase().includes(term) || u.role?.toLowerCase().includes(term) || u.email?.toLowerCase().includes(term)).slice(0, 10)
    );
  }, [employeeSearchTerm, users]);

  useEffect(() => {
    if (!inchargeSearchTerm.trim()) { setFilteredInchargeEmployees([]); return; }
    const term = inchargeSearchTerm.toLowerCase();
    setFilteredInchargeEmployees(
      users.filter(u => u.name?.toLowerCase().includes(term) || u.role?.toLowerCase().includes(term) || u.email?.toLowerCase().includes(term)).slice(0, 10)
    );
  }, [inchargeSearchTerm, users]);

  const fetchUsers = async () => {
    try {
      const snap = await getDocs(collection(db, 'users'));
      setUsers(snap.docs.map(d => ({ id: d.id, name: d.data().name || 'Unnamed', role: d.data().role || 'worker', email: d.data().email || '' })));
    } catch (e) { console.error('Error fetching users:', e); }
  };

  const fetchClients = async () => {
    try {
      const snap = await getDocs(collection(db, 'clients'));
      setClientsList(snap.docs.map(d => ({ id: d.id, client: d.data().name || d.data().client || 'Unnamed' })));
    } catch (e) { console.error('Error fetching clients:', e); }
  };

  const fetchPlannerData = async () => {
    setLoading(true);
    try {
      const snap = await getDocs(collection(db, 'projectPlanner'));
      if (snap.empty) { setPlannerData([]); return; }
      const ensureArray = (field) => {
        if (!field) return [];
        if (Array.isArray(field)) return field;
        if (typeof field === 'string') return field ? [field] : [];
        return [];
      };
      let data = snap.docs.map(d => ({
        id: d.id,
        carpenter:   d.data().carpenter   || '',
        currentWork: ensureArray(d.data().currentWork),
        nextPlanned: ensureArray(d.data().nextPlanned),
        preparation: ensureArray(d.data().preparation),
        pipeLine:    ensureArray(d.data().pipeLine),
        incharge:    ensureArray(d.data().incharge),
        order:       d.data().order !== undefined ? d.data().order : null,
      }));
      const hasOrder = data.some(r => r.order !== null);
      if (!hasOrder) {
        data = data.map((r, i) => ({ ...r, order: i }));
        for (const r of data) await updateDoc(doc(db, 'projectPlanner', r.id), { order: r.order });
      }
      data.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
      setPlannerData(data);
      await runAutoMove(data);
    } catch (e) {
      console.error('Error fetching planner data:', e);
    } finally {
      setLoading(false);
    }
  };

  // ========== AUTO-MOVE LOGIC ==========
  const runAutoMove = async (dataToCheck = null) => {
    if (isAutoMoving.current) return;
    isAutoMoving.current = true;
    try {
      const currentData = dataToCheck || plannerData;
      const today = new Date().toISOString().split('T')[0];
      let hasChanges = false;

      const newData = currentData.map(row => {
        const columns = ['currentWork', 'nextPlanned', 'preparation', 'pipeLine'];
        const itemsToMove = [];

        columns.forEach(col => {
          const items = row[col] || [];
          items.forEach(item => {
            if (typeof item === 'object') {
              const { startDate, endDate } = item;
              if (col !== 'currentWork') {
                if ((startDate && startDate <= today) || (endDate && endDate < today)) {
                  itemsToMove.push({ item, sourceCol: col });
                }
              }
            }
          });
        });

        if (itemsToMove.length === 0) return row;

        const newRow = { ...row };
        columns.forEach(col => {
          if (col === 'currentWork') return;
          const items = newRow[col] || [];
          newRow[col] = items.filter(item => !itemsToMove.some(m => m.item === item));
        });
        const currentWork = newRow.currentWork || [];
        itemsToMove.forEach(({ item }) => {
          if (!currentWork.some(i => i === item)) {
            currentWork.push(item);
          }
        });
        newRow.currentWork = currentWork;
        hasChanges = true;
        return newRow;
      });

      if (hasChanges) {
        setPlannerData(newData);
        const updates = newData.map((row, index) => {
          const oldRow = currentData[index];
          if (row !== oldRow) {
            const fields = ['currentWork', 'nextPlanned', 'preparation', 'pipeLine'];
            const updateData = {};
            fields.forEach(f => {
              updateData[f] = row[f] || [];
            });
            return updateDoc(doc(db, 'projectPlanner', row.id), updateData);
          }
          return null;
        }).filter(Boolean);
        if (updates.length > 0) {
          await Promise.all(updates);
        }
      }
    } catch (e) {
      console.error('Auto-move error:', e);
    } finally {
      isAutoMoving.current = false;
    }
  };

  // ========== SYNC HELPERS ==========
  const syncTaskToClient = async (taskText, clientId, carpenterId, carpenterName, operation) => {
    if (!clientId) return;
    const clientRef = doc(db, 'clients', clientId);
    const taskObj = { text: taskText, carpenterId };
    if (operation === 'add') {
      await updateDoc(clientRef, { items: arrayUnion(taskObj) });
      if (carpenterId && carpenterName) {
        await updateDoc(clientRef, { carpenters: arrayUnion(carpenterName) });
      }
    } else if (operation === 'remove') {
      await updateDoc(clientRef, { items: arrayRemove(taskObj) });
    }
  };

  // ========== CRUD ==========
  const handleAddItem = async (rowId, field) => {
    if (!newItemText.trim()) { alert('Please enter an item'); return; }
    const row = plannerData.find(r => r.id === rowId);
    const carpenterName = row ? row.carpenter : null;
    const taskObj = {
      text: newItemText.trim(),
      clientId: newItemClientId || null,
      carpenterId: rowId,
    };
    if (newItemStartDate) taskObj.startDate = newItemStartDate;
    if (newItemEndDate) taskObj.endDate = newItemEndDate;

    try {
      await updateDoc(doc(db, 'projectPlanner', rowId), { [field]: arrayUnion(taskObj) });
      setPlannerData(prev => prev.map(r =>
        r.id === rowId ? { ...r, [field]: [...(r[field] || []), taskObj] } : r
      ));
      if (field !== 'pipeLine' && newItemClientId) {
        await syncTaskToClient(newItemText.trim(), newItemClientId, rowId, carpenterName, 'add');
      }
      setNewItemText('');
      setNewItemClientId('');
      setNewItemStartDate('');
      setNewItemEndDate('');
      setAddingItem(null);
      await runAutoMove();
    } catch (e) { console.error('Error adding item:', e); alert('Failed to add item'); }
  };

  const handleRemoveItem = async (rowId, field, itemToRemove) => {
    const text = typeof itemToRemove === 'object' ? itemToRemove.text : itemToRemove;
    if (!window.confirm(`Remove "${text}"?`)) return;
    try {
      await updateDoc(doc(db, 'projectPlanner', rowId), { [field]: arrayRemove(itemToRemove) });
      setPlannerData(prev => prev.map(r =>
        r.id === rowId ? { ...r, [field]: (r[field] || []).filter(i => i !== itemToRemove) } : r
      ));
      if (typeof itemToRemove === 'object' && itemToRemove.clientId) {
        await syncTaskToClient(text, itemToRemove.clientId, rowId, null, 'remove');
      }
    } catch (e) { console.error('Error removing item:', e); alert('Failed to remove item'); }
  };

  const startEditItem = (rowId, field, index, currentValue) => {
    setEditingItem({ rowId, field, index });
    const text = typeof currentValue === 'object' ? currentValue.text : currentValue;
    setEditItemValue(text);
  };

  const cancelEditItem = () => { setEditingItem(null); setEditItemValue(''); };

  const saveEditItem = async (rowId, field, index, newValue) => {
    if (!newValue.trim()) { alert('Item cannot be empty'); return; }
    const row = plannerData.find(r => r.id === rowId);
    if (!row) return;
    const oldArray = row[field] || [];
    const oldItem = oldArray[index];
    const oldText = typeof oldItem === 'object' ? oldItem.text : oldItem;
    if (oldText === newValue.trim()) { cancelEditItem(); return; }
    const newArray = [...oldArray];
    const newItem = typeof oldItem === 'object'
      ? { ...oldItem, text: newValue.trim() }
      : newValue.trim();
    newArray[index] = newItem;

    try {
      await updateDoc(doc(db, 'projectPlanner', rowId), { [field]: newArray });
      setPlannerData(prev => prev.map(r => r.id === rowId ? { ...r, [field]: newArray } : r));

      if (typeof oldItem === 'object' && oldItem.clientId) {
        const clientRef = doc(db, 'clients', oldItem.clientId);
        const oldObj = { text: oldText, carpenterId: rowId };
        const newObj = { text: newValue.trim(), carpenterId: rowId };
        await updateDoc(clientRef, { items: arrayRemove(oldObj) });
        await updateDoc(clientRef, { items: arrayUnion(newObj) });
      }
      cancelEditItem();
      await runAutoMove();
    } catch (e) { console.error('Error editing item:', e); alert('Failed to edit item'); }
  };

  const handleItemReorder = async (rowId, field, fromIndex, toIndex) => {
    if (fromIndex === toIndex) return;
    const row = plannerData.find(r => r.id === rowId);
    if (!row) return;
    const arr = [...(row[field] || [])];
    const [moved] = arr.splice(fromIndex, 1);
    arr.splice(toIndex, 0, moved);
    try {
      await updateDoc(doc(db, 'projectPlanner', rowId), { [field]: arr });
      setPlannerData(prev => prev.map(r => r.id === rowId ? { ...r, [field]: arr } : r));
    } catch (e) { console.error('Error reordering items:', e); }
  };

  // ========== DRAG & DROP HELPER ==========
  const moveItemToCell = async (sourceRowId, sourceField, item, targetRowId, targetField) => {
    if (isProcessingDrop.current) return;
    if (sourceRowId === targetRowId && sourceField === targetField) return;

    isProcessingDrop.current = true;

    const sourceRow = plannerData.find(r => r.id === sourceRowId);
    if (!sourceRow) { isProcessingDrop.current = false; return; }

    const itemText = typeof item === 'object' ? item.text : item;
    const actualItem = (sourceRow[sourceField] || []).find(i => {
      if (typeof i === 'object' && typeof item === 'object') {
        return i.text === item.text && i.clientId === item.clientId && i.carpenterId === item.carpenterId;
      }
      return i === itemText;
    });

    if (!actualItem) {
      console.warn('Item not found in source array, aborting move.');
      isProcessingDrop.current = false;
      return;
    }

    setPlannerData(prev => {
      const sourceRow = prev.find(r => r.id === sourceRowId);
      const targetRow = prev.find(r => r.id === targetRowId);
      if (!sourceRow || !targetRow) return prev;

      return prev.map(row => {
        if (row.id === sourceRowId && row.id === targetRowId) {
          return {
            ...row,
            [sourceField]: (row[sourceField] || []).filter(i => i !== actualItem),
            [targetField]: [...(row[targetField] || []), actualItem],
          };
        }
        if (row.id === sourceRowId) {
          return { ...row, [sourceField]: (row[sourceField] || []).filter(i => i !== actualItem) };
        }
        if (row.id === targetRowId) {
          return { ...row, [targetField]: [...(row[targetField] || []), actualItem] };
        }
        return row;
      });
    });

    try {
      const sourceRef = doc(db, 'projectPlanner', sourceRowId);
      const targetRef = doc(db, 'projectPlanner', targetRowId);

      if (sourceRowId === targetRowId) {
        await updateDoc(sourceRef, { [sourceField]: arrayRemove(actualItem) });
        await updateDoc(sourceRef, { [targetField]: arrayUnion(actualItem) });
      } else {
        await Promise.all([
          updateDoc(sourceRef, { [sourceField]: arrayRemove(actualItem) }),
          updateDoc(targetRef, { [targetField]: arrayUnion(actualItem) }),
        ]);
      }

      if (typeof actualItem === 'object' && actualItem.clientId && sourceRowId !== targetRowId) {
        const clientRef = doc(db, 'clients', actualItem.clientId);
        const oldObj = { text: actualItem.text, carpenterId: sourceRowId };
        const newObj = { text: actualItem.text, carpenterId: targetRowId };
        await updateDoc(clientRef, { items: arrayRemove(oldObj) });
        await updateDoc(clientRef, { items: arrayUnion(newObj) });
        const targetRow = plannerData.find(r => r.id === targetRowId);
        const targetName = targetRow ? targetRow.carpenter : null;
        if (targetName) {
          await updateDoc(clientRef, { carpenters: arrayUnion(targetName) });
        }
      }
      await runAutoMove();
    } catch (error) {
      console.error('Firestore sync failed, rolling back:', error);
      await fetchPlannerData();
      alert('Failed to move item. Changes have been reverted.');
    } finally {
      isProcessingDrop.current = false;
    }
  };

  // ========== SHIFT TASK MODAL ==========
  const handleShiftTask = async () => {
    if (!shiftTargetField || !shiftSource.rowId) return;
    const item = shiftSource.item;
    const itemText = typeof item === 'object' ? item.text : item;
    try {
      const docRef = doc(db, 'projectPlanner', shiftSource.rowId);
      await updateDoc(docRef, { [shiftSource.field]: arrayRemove(item) });
      await updateDoc(docRef, { [shiftTargetField]: arrayUnion(item) });
      setPlannerData(prev => prev.map(row => {
        if (row.id !== shiftSource.rowId) return row;
        return {
          ...row,
          [shiftSource.field]:  (row[shiftSource.field]  || []).filter(i => i !== item),
          [shiftTargetField]:   [...(row[shiftTargetField] || []), item],
        };
      }));
      setShowShiftModal(false);
      setShiftSource({ rowId: null, field: null, item: null });
      setShiftTargetField('');
      alert(`Task shifted to ${columnLabels[shiftTargetField]}`);
      await runAutoMove();
    } catch (e) { console.error('Error shifting task:', e); alert('Failed to shift task'); }
  };

  const openShiftModal = (rowId, field, item) => {
    setShiftSource({ rowId, field, item });
    setShiftTargetField('');
    setShowShiftModal(true);
  };

  // ========== INCHARGE ==========
  const handleAddIncharge = async (rowId, selectedEmployee = null) => {
    const inchargeName = (selectedEmployee ? selectedEmployee.name : newInchargeName).trim();
    if (!inchargeName) { alert('Please select or enter a name'); return; }
    try {
      await updateDoc(doc(db, 'projectPlanner', rowId), { incharge: arrayUnion(inchargeName) });
      setPlannerData(prev => prev.map(row =>
        row.id === rowId ? { ...row, incharge: [...(row.incharge || []), inchargeName] } : row
      ));
      setNewInchargeName(''); setInchargeSearchTerm('');
      setShowInchargeSearchModal(false); setCurrentInchargeRowId(null);
    } catch (e) { console.error('Error adding incharge:', e); alert('Failed to add incharge'); }
  };

  const handleRemoveIncharge = async (rowId, personToRemove) => {
    if (!window.confirm(`Remove "${personToRemove}" from incharge?`)) return;
    try {
      await updateDoc(doc(db, 'projectPlanner', rowId), { incharge: arrayRemove(personToRemove) });
      setPlannerData(prev => prev.map(row =>
        row.id === rowId ? { ...row, incharge: (row.incharge || []).filter(p => p !== personToRemove) } : row
      ));
    } catch (e) { console.error('Error removing incharge:', e); alert('Failed to remove incharge'); }
  };

  // ========== CARPENTER ==========
  const startEditCarpenter = (rowId, currentName) => { setEditingCarpenter({ rowId, currentName }); setEditCarpenterValue(currentName); };

  const saveEditCarpenter = async () => {
    if (!editCarpenterValue.trim()) { alert('Carpenter name cannot be empty'); return; }
    try {
      await updateDoc(doc(db, 'projectPlanner', editingCarpenter.rowId), { carpenter: editCarpenterValue.trim() });
      setPlannerData(prev => prev.map(row =>
        row.id === editingCarpenter.rowId ? { ...row, carpenter: editCarpenterValue.trim() } : row
      ));
      setEditingCarpenter(null); setEditCarpenterValue('');
    } catch (e) { console.error('Error updating carpenter:', e); alert('Failed to update carpenter name'); }
  };

  const handleDeleteRow = async (rowId, carpenterName) => {
    if (!window.confirm(`Delete row for "${carpenterName}"? This cannot be undone.`)) return;
    try {
      await deleteDoc(doc(db, 'projectPlanner', rowId));
      setPlannerData(prev => prev.filter(row => row.id !== rowId));
    } catch (e) { console.error('Error deleting row:', e); alert('Failed to delete row'); }
  };

  const handleAddRow = async (selectedEmployee = null) => {
    const carpenterName = (selectedEmployee ? selectedEmployee.name : newCarpenterName).trim();
    if (!carpenterName) { alert('Please select or enter a carpenter name'); return; }
    if (plannerData.some(row => row.carpenter.toLowerCase() === carpenterName.toLowerCase())) {
      alert(`"${carpenterName}" is already in the planner.`); return;
    }
    try {
      const maxOrder = plannerData.reduce((max, row) => Math.max(max, row.order ?? 0), 0);
      const newRow = { carpenter: carpenterName, currentWork: [], nextPlanned: [], preparation: [], pipeLine: [], incharge: [], order: maxOrder + 1 };
      const docRef = await addDoc(collection(db, 'projectPlanner'), newRow);
      setPlannerData(prev => [...prev, { id: docRef.id, ...newRow }]);
      setNewCarpenterName(''); setEmployeeSearchTerm(''); setShowAddRowModal(false);
    } catch (e) { console.error('Error adding row:', e); alert('Failed to add row'); }
  };

  const openInchargeModal = (rowId) => { setCurrentInchargeRowId(rowId); setInchargeSearchTerm(''); setNewInchargeName(''); setShowInchargeSearchModal(true); };

  // ========== DRAG EVENT HANDLERS ==========
  const handleDragStart = (e, type, rowId, field = null, item = null, fromIndex = null) => {
    e.dataTransfer.setData('text/plain', JSON.stringify({ type, rowId, field, item, fromIndex }));
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDragEnter = (e, rowId, field) => {
    e.preventDefault();
    setDragOverRowId(rowId);
    setDragOverField(field);
  };

  const handleDragLeave = (e) => {
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDragOverRowId(null);
      setDragOverField(null);
      setDragOverItemIndex(null);
    }
  };

  const handleDropOnColumn = async (e, targetRowId, targetField) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverRowId(null);
    setDragOverField(null);
    setDragOverItemIndex(null);

    const raw = e.dataTransfer.getData('text/plain');
    if (!raw) return;
    let data;
    try { data = JSON.parse(raw); } catch { return; }
    if (data.type !== 'task') return;
    if (data.rowId === targetRowId && data.field === targetField) return;

    await moveItemToCell(data.rowId, data.field, data.item, targetRowId, targetField);
  };

  const handleDropOnCarpenter = async (e, targetRowId) => {
    e.preventDefault();
    setDragOverRowId(null);
    setDragOverField(null);
    const raw = e.dataTransfer.getData('text/plain');
    if (!raw) return;
    let data;
    try { data = JSON.parse(raw); } catch { return; }
    if (data.type !== 'row' || data.rowId === targetRowId) return;
    const sourceRow = plannerData.find(r => r.id === data.rowId);
    const targetRow = plannerData.find(r => r.id === targetRowId);
    if (!sourceRow || !targetRow) return;
    try {
      await updateDoc(doc(db, 'projectPlanner', sourceRow.id), { order: targetRow.order });
      await updateDoc(doc(db, 'projectPlanner', targetRow.id), { order: sourceRow.order });
      setPlannerData(prev =>
        prev.map(row => {
          if (row.id === sourceRow.id) return { ...row, order: targetRow.order };
          if (row.id === targetRow.id) return { ...row, order: sourceRow.order };
          return row;
        }).sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
      );
    } catch (e) { console.error('Error reordering rows:', e); alert('Failed to reorder rows'); }
  };

  // ========== HELPER: days left / overdue ==========
  const getDaysTag = (startDate, endDate) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (startDate) {
      const start = new Date(startDate + 'T00:00:00');
      const diffTime = start - today;
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      if (diffDays > 0) {
        return <span style={{ color: '#2563eb', fontWeight: '500', fontSize: '10px', marginLeft: '6px' }}>⏳ Starts in {diffDays}d</span>;
      }
    }

    if (!endDate) return null;
    const end = new Date(endDate + 'T00:00:00');
    const diffTime = end - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays < 0) {
      return <span style={{ color: '#b91c1c', fontWeight: 'bold', fontSize: '10px', marginLeft: '6px' }}>⚠️ {-diffDays}d overdue</span>;
    } else if (diffDays === 0) {
      return <span style={{ color: '#d97706', fontWeight: 'bold', fontSize: '10px', marginLeft: '6px' }}>⏳ Today</span>;
    } else {
      return <span style={{ color: '#059669', fontWeight: '500', fontSize: '10px', marginLeft: '6px' }}>⏳ {diffDays}d left</span>;
    }
  };

  // ========== RENDER HELPERS ==========
  const todayStr = new Date().toISOString().split('T')[0];

  const renderMultiItemCell = (rowId, items, field, placeholder, borderColor) => {
    const itemList = Array.isArray(items) ? items : [];
    const canShift = field !== 'incharge';

    return (
      <div style={{ minWidth: '180px' }}>
        {itemList.length > 0 ? (
          itemList.map((item, idx) => {
            const itemText = typeof item === 'object' ? item.text : item;
            const clientId = typeof item === 'object' ? item.clientId : null;
            const client = clientId ? clientsList.find(c => c.id === clientId) : null;
            const clientName = client ? client.client : null;
            const startDate = typeof item === 'object' ? item.startDate : null;
            const endDate = typeof item === 'object' ? item.endDate : null;
            const isOverdue = endDate && endDate < todayStr;

            const isEditing = editingItem?.rowId === rowId && editingItem?.field === field && editingItem?.index === idx;
            return (
              <div
                key={`${itemText}-${idx}`}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  background: isEditing
                    ? '#f0f9ff'
                    : (dragOverRowId === rowId && dragOverField === field && dragOverItemIndex === idx ? '#e6f0ff' : '#ffffff'),
                  padding: '6px 10px',
                  marginBottom: '10px',
                  borderRadius: '6px',
                  fontSize: '12px',
                  border: `1px solid ${borderColor || '#e2e8f0'}`,
                  boxShadow: '0 1px 2px rgba(0,0,0,0.02)',
                  cursor: isEditing ? 'default' : 'grab',
                }}
                draggable={!isEditing}
                onDragStart={(e) => {
                  if (!isEditing) handleDragStart(e, 'task', rowId, field, item, idx);
                }}
                onDragOver={(e) => {
                  if (!isEditing) {
                    e.preventDefault();
                    e.stopPropagation();
                    e.dataTransfer.dropEffect = 'move';
                    setDragOverRowId(rowId);
                    setDragOverField(field);
                    setDragOverItemIndex(idx);
                  }
                }}
                onDragLeave={(e) => {
                  e.stopPropagation();
                  if (!e.currentTarget.contains(e.relatedTarget)) {
                    setDragOverItemIndex(null);
                  }
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setDragOverRowId(null);
                  setDragOverField(null);
                  setDragOverItemIndex(null);

                  const raw = e.dataTransfer.getData('text/plain');
                  if (!raw) return;
                  let data;
                  try { data = JSON.parse(raw); } catch { return; }
                  if (data.type !== 'task') return;

                  if (data.rowId === rowId && data.field === field) {
                    if (data.fromIndex !== undefined && data.fromIndex !== idx) {
                      handleItemReorder(rowId, field, data.fromIndex, idx);
                    }
                  } else {
                    moveItemToCell(data.rowId, data.field, data.item, rowId, field);
                  }
                }}
              >
                {isEditing ? (
                  <div style={{ display: 'flex', gap: '6px', flex: 1 }}>
                    <input
                      type="text"
                      value={editItemValue}
                      onChange={(e) => setEditItemValue(e.target.value)}
                      onKeyPress={(e) => { if (e.key === 'Enter') saveEditItem(rowId, field, idx, editItemValue); }}
                      autoFocus
                      style={{ flex: 1, padding: '4px 6px', borderRadius: '4px', border: '1px solid #2563eb', outline: 'none', fontSize: '12px' }}
                    />
                    <button onClick={() => saveEditItem(rowId, field, idx, editItemValue)} style={{ background: '#10b981', color: 'white', border: 'none', padding: '2px 8px', borderRadius: '4px', cursor: 'pointer', fontSize: '11px' }}>Save</button>
                    <button onClick={cancelEditItem} style={{ background: '#9ca3af', color: 'white', border: 'none', padding: '2px 8px', borderRadius: '4px', cursor: 'pointer', fontSize: '11px' }}>Cancel</button>
                  </div>
                ) : (
                  <>
                    <span style={{ flex: 1, wordBreak: 'break-word' }}>
                      {itemText}
                      {clientName && (
                        <span style={{
                          color: '#64748b',
                          marginLeft: '8px',
                          fontSize: '10px',
                          fontStyle: 'italic',
                          borderLeft: '1px solid #e2e8f0',
                          paddingLeft: '8px'
                        }}>
                          {clientName}
                        </span>
                      )}
                      {getDaysTag(startDate, endDate)}
                      {isOverdue && (
                        <span style={{ color: 'red', fontWeight: 'bold', fontSize: '10px', marginLeft: '6px' }}>
                          🔴 Over Due
                        </span>
                      )}
                    </span>
                    <div style={{ display: 'flex', gap: '4px' }}>
                      <button onClick={() => startEditItem(rowId, field, idx, item)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '12px', color: '#2563eb', padding: '2px 6px', borderRadius: '4px' }} title="Edit item">✏️</button>
                      <button onClick={() => handleRemoveItem(rowId, field, item)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '14px', color: '#dc2626', padding: '2px 6px', borderRadius: '4px' }} title="Remove item">✕</button>
                    </div>
                  </>
                )}
              </div>
            );
          })
        ) : (
          <div style={{ color: '#94a3b8', fontSize: '12px', fontStyle: 'italic', padding: '8px', textAlign: 'center', background: '#f8fafc', borderRadius: '6px', marginBottom: '6px' }}>
            {placeholder || 'No items'}
          </div>
        )}

        {addingItem?.rowId === rowId && addingItem?.field === field ? (
          <div style={{ display: 'flex', gap: '6px', marginTop: '8px', flexWrap: 'wrap' }}>
            <input
              type="text"
              value={newItemText}
              onChange={(e) => setNewItemText(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleAddItem(rowId, field)}
              placeholder="Enter task..."
              autoFocus
              style={{ flex: '2 1 150px', padding: '8px 10px', borderRadius: '8px', border: '1px solid #800020', outline: 'none', fontSize: '12px' }}
            />
            <select
              value={newItemClientId}
              onChange={(e) => setNewItemClientId(e.target.value)}
              style={{ flex: '1 1 120px', padding: '8px 10px', borderRadius: '8px', border: '1px solid #e2e8f0', outline: 'none', fontSize: '12px', background: 'white' }}
            >
              <option value="">No Client</option>
              {clientsList.map(c => <option key={c.id} value={c.id}>{c.client}</option>)}
            </select>
            <input
              type="date"
              value={newItemStartDate}
              onChange={(e) => setNewItemStartDate(e.target.value)}
              style={{ padding: '8px 10px', borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '12px', flex: '0 1 140px' }}
              title="Start Date"
            />
            <input
              type="date"
              value={newItemEndDate}
              onChange={(e) => setNewItemEndDate(e.target.value)}
              style={{ padding: '8px 10px', borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '12px', flex: '0 1 140px' }}
              title="End Date"
            />
            <button onClick={() => handleAddItem(rowId, field)} style={{ background: '#10b981', color: 'white', border: 'none', padding: '6px 12px', borderRadius: '6px', cursor: 'pointer', fontSize: '11px', fontWeight: '500' }}>Add</button>
            <button
              onClick={() => {
                setAddingItem(null);
                setNewItemText('');
                setNewItemClientId('');
                setNewItemStartDate('');
                setNewItemEndDate('');
              }}
              style={{ background: '#9ca3af', color: 'white', border: 'none', padding: '6px 12px', borderRadius: '6px', cursor: 'pointer', fontSize: '11px', fontWeight: '500' }}
            >Cancel</button>
          </div>
        ) : (
          <button
            onClick={() => setAddingItem({ rowId, field })}
            style={{ background: 'none', border: '1px dashed #cbd5e1', padding: '6px 10px', borderRadius: '8px', cursor: 'pointer', fontSize: '11px', color: '#64748b', width: '100%', marginTop: '6px', transition: 'all 0.2s ease' }}
            onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#800020'; e.currentTarget.style.color = '#800020'; }}
            onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#cbd5e1'; e.currentTarget.style.color = '#64748b'; }}
          >+ Add Item</button>
        )}
      </div>
    );
  };

  const renderInchargeCell = (rowId, inchargeList) => {
    const list = Array.isArray(inchargeList) ? inchargeList : [];
    return (
      <div style={{ minWidth: '140px' }}>
        {list.length > 0 ? list.map((person, idx) => (
          <div key={idx} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#ffffff', padding: '6px 10px', marginBottom: '6px', borderRadius: '6px', fontSize: '12px', border: `1px solid ${columnColors.incharge.border}` }}>
            <span style={{ flex: 1 }}>👤 {person}</span>
            <button onClick={() => handleRemoveIncharge(rowId, person)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '14px', color: '#dc2626', padding: '2px 6px', borderRadius: '4px' }} title="Remove incharge">✕</button>
          </div>
        )) : (
          <div style={{ color: '#94a3b8', fontSize: '12px', fontStyle: 'italic', padding: '8px', textAlign: 'center', background: '#f8fafc', borderRadius: '6px', marginBottom: '6px' }}>No incharge assigned</div>
        )}
        <button
          onClick={() => openInchargeModal(rowId)}
          style={{ background: 'none', border: '1px dashed #cbd5e1', padding: '6px 10px', borderRadius: '8px', cursor: 'pointer', fontSize: '11px', color: '#64748b', width: '100%', marginTop: '6px', transition: 'all 0.2s ease' }}
          onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#800020'; e.currentTarget.style.color = '#800020'; }}
          onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#cbd5e1'; e.currentTarget.style.color = '#64748b'; }}
        >+ Add Incharge</button>
      </div>
    );
  };

  // ========== MAIN RENDER ==========
  const filteredData = plannerData.filter(row => row.carpenter?.toLowerCase().includes(searchTerm.toLowerCase()));
  const canEdit = ['manager', 'ceo', 'managing-director', 'quality-control'].includes(userRole);

  if (loading) {
    return (
      <div className="loading-state">
        <div className="loading-spinner"></div>
        <p>Loading project planner...</p>
      </div>
    );
  }

  const renderPlanner = () => (
    <>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '16px' }}>
        <div>
          <h1 style={{ fontSize: '28px', fontWeight: '700', margin: 0, color: '#1e293b' }}>📋 Project Planner</h1>
          <p style={{ color: '#64748b', marginTop: '4px' }}>Plan and track tasks for carpenters across all stages</p>
        </div>
        <div style={{ display: 'flex', gap: '12px' }}>
          <input
            type="text"
            placeholder="🔍 Search carpenter..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ padding: '10px 16px', borderRadius: '12px', border: '1px solid #e2e8f0', width: '250px', outline: 'none', fontSize: '14px' }}
          />
          <button onClick={exportToPDF} style={{ background: '#800020', color: 'white', border: 'none', padding: '10px 20px', borderRadius: '12px', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}>📄 Export PDF</button>
          {canEdit && (
            <button onClick={() => setShowAddRowModal(true)} style={{ background: 'linear-gradient(135deg, #800020, #5a0016)', color: 'white', border: 'none', padding: '10px 20px', borderRadius: '12px', fontWeight: '600', cursor: 'pointer' }}>+ Add Carpenter</button>
          )}
        </div>
      </div>

      {/* Add Carpenter Modal */}
      {showAddRowModal && (
        <div className="modal-overlay" onClick={() => setShowAddRowModal(false)}>
          <div className="modal-content" style={{ maxWidth: '500px', borderRadius: '20px' }} onClick={e => e.stopPropagation()}>
            <h3 style={{ color: '#800020' }}>➕ Add Carpenter</h3>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#334155' }}>Search Employee</label>
            <div style={{ position: 'relative' }}>
              <input type="text" placeholder="🔍 Search by name, role, or email..." value={employeeSearchTerm} onChange={e => setEmployeeSearchTerm(e.target.value)} style={{ width: '100%', padding: '12px', borderRadius: '10px', border: '1px solid #e2e8f0', fontSize: '14px', marginBottom: '12px', outline: 'none' }} />
              {employeeSearchTerm && filteredEmployees.length > 0 && (
                <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: 'white', border: '1px solid #e2e8f0', borderRadius: '10px', maxHeight: '200px', overflowY: 'auto', zIndex: 1000, boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
                  {filteredEmployees.map(emp => (
                    <div key={emp.id} onClick={() => handleAddRow(emp)} style={{ padding: '12px', cursor: 'pointer', borderBottom: '1px solid #f0f0f0' }} onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'} onMouseLeave={e => e.currentTarget.style.background = 'white'}>
                      <div style={{ fontWeight: '500', color: '#1e293b' }}>{emp.name}</div>
                      <div style={{ fontSize: '11px', color: '#64748b' }}>{emp.role?.replace('-', ' ')} • {emp.email}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div style={{ textAlign: 'center', margin: '12px 0', color: '#94a3b8', fontSize: '12px' }}>— OR —</div>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#334155' }}>Enter Manually</label>
            <input type="text" value={newCarpenterName} onChange={e => setNewCarpenterName(e.target.value)} placeholder="Enter carpenter name manually" style={{ width: '100%', padding: '12px', borderRadius: '10px', border: '1px solid #e2e8f0', marginBottom: '20px', fontSize: '14px', outline: 'none' }} onKeyPress={e => e.key === 'Enter' && handleAddRow()} />
            <div style={{ display: 'flex', gap: '12px' }}>
              <button onClick={() => handleAddRow()} style={{ flex: 1, background: '#800020', color: 'white', border: 'none', padding: '12px', borderRadius: '10px', fontWeight: '600', cursor: 'pointer' }}>Add Carpenter</button>
              <button onClick={() => { setShowAddRowModal(false); setEmployeeSearchTerm(''); setNewCarpenterName(''); }} style={{ flex: 1, background: '#f1f5f9', color: '#475569', border: 'none', padding: '12px', borderRadius: '10px', fontWeight: '600', cursor: 'pointer' }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Shift Modal */}
      {showShiftModal && (
        <div className="modal-overlay" onClick={() => setShowShiftModal(false)}>
          <div className="modal-content" style={{ maxWidth: '450px', borderRadius: '20px' }} onClick={e => e.stopPropagation()}>
            <h3 style={{ color: '#800020' }}>↪️ Move Task to Different Column</h3>
            <p style={{ marginBottom: '16px', color: '#475569' }}>Moving: <strong>"{typeof shiftSource.item === 'object' ? shiftSource.item.text : shiftSource.item}"</strong></p>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#334155' }}>Select destination column:</label>
            <select value={shiftTargetField} onChange={e => setShiftTargetField(e.target.value)} style={{ width: '100%', padding: '12px', borderRadius: '10px', border: '1px solid #e2e8f0', fontSize: '14px', marginBottom: '20px', outline: 'none', background: 'white' }}>
              <option value="">Select a column...</option>
              {columnOrder.filter(col => col !== shiftSource.field).map(col => <option key={col} value={col}>{columnLabels[col]}</option>)}
            </select>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button onClick={handleShiftTask} disabled={!shiftTargetField} style={{ flex: 1, background: '#2563eb', color: 'white', border: 'none', padding: '12px', borderRadius: '10px', fontWeight: '600', cursor: shiftTargetField ? 'pointer' : 'not-allowed', opacity: shiftTargetField ? 1 : 0.5 }}>Move Task</button>
              <button onClick={() => { setShowShiftModal(false); setShiftSource({ rowId: null, field: null, item: null }); setShiftTargetField(''); }} style={{ flex: 1, background: '#f1f5f9', color: '#475569', border: 'none', padding: '12px', borderRadius: '10px', fontWeight: '600', cursor: 'pointer' }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Add Incharge Modal */}
      {showInchargeSearchModal && (
        <div className="modal-overlay" onClick={() => setShowInchargeSearchModal(false)}>
          <div className="modal-content" style={{ maxWidth: '500px', borderRadius: '20px' }} onClick={e => e.stopPropagation()}>
            <h3 style={{ color: '#800020' }}>👥 Add Incharge Person</h3>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#334155' }}>Search Employee</label>
            <div style={{ position: 'relative' }}>
              <input type="text" placeholder="🔍 Search by name, role, or email..." value={inchargeSearchTerm} onChange={e => setInchargeSearchTerm(e.target.value)} style={{ width: '100%', padding: '12px', borderRadius: '10px', border: '1px solid #e2e8f0', fontSize: '14px', marginBottom: '12px', outline: 'none' }} />
              {inchargeSearchTerm && filteredInchargeEmployees.length > 0 && (
                <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: 'white', border: '1px solid #e2e8f0', borderRadius: '10px', maxHeight: '200px', overflowY: 'auto', zIndex: 1000, boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
                  {filteredInchargeEmployees.map(emp => (
                    <div key={emp.id} onClick={() => handleAddIncharge(currentInchargeRowId, emp)} style={{ padding: '12px', cursor: 'pointer', borderBottom: '1px solid #f0f0f0' }} onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'} onMouseLeave={e => e.currentTarget.style.background = 'white'}>
                      <div style={{ fontWeight: '500', color: '#1e293b' }}>{emp.name}</div>
                      <div style={{ fontSize: '11px', color: '#64748b' }}>{emp.role?.replace('-', ' ')} • {emp.email}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div style={{ textAlign: 'center', margin: '12px 0', color: '#94a3b8', fontSize: '12px' }}>— OR —</div>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#334155' }}>Enter Manually</label>
            <input type="text" value={newInchargeName} onChange={e => setNewInchargeName(e.target.value)} placeholder="Enter name manually" style={{ width: '100%', padding: '12px', borderRadius: '10px', border: '1px solid #e2e8f0', marginBottom: '20px', fontSize: '14px', outline: 'none' }} onKeyPress={e => e.key === 'Enter' && handleAddIncharge(currentInchargeRowId)} />
            <div style={{ display: 'flex', gap: '12px' }}>
              <button onClick={() => handleAddIncharge(currentInchargeRowId)} style={{ flex: 1, background: '#800020', color: 'white', border: 'none', padding: '12px', borderRadius: '10px', fontWeight: '600', cursor: 'pointer' }}>Add Incharge</button>
              <button onClick={() => { setShowInchargeSearchModal(false); setInchargeSearchTerm(''); setNewInchargeName(''); setCurrentInchargeRowId(null); }} style={{ flex: 1, background: '#f1f5f9', color: '#475569', border: 'none', padding: '12px', borderRadius: '10px', fontWeight: '600', cursor: 'pointer' }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Empty State */}
      {plannerData.length === 0 && (
        <div style={{ textAlign: 'center', padding: '80px', background: 'white', borderRadius: '20px', border: '1px solid #e2e8f0' }}>
          <div style={{ fontSize: '64px', marginBottom: '16px' }}>📋</div>
          <h3 style={{ marginBottom: '8px', color: '#1e293b' }}>No Data Yet</h3>
          <p style={{ color: '#64748b', marginBottom: '24px' }}>Click "Add Carpenter" to start planning your projects</p>
          {canEdit && <button onClick={() => setShowAddRowModal(true)} style={{ background: '#800020', color: 'white', border: 'none', padding: '12px 24px', borderRadius: '12px', fontWeight: '600', cursor: 'pointer' }}>+ Add First Carpenter</button>}
        </div>
      )}

      {/* Data Table */}
      {plannerData.length > 0 && (
        <div style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: 'calc(100vh - 200px)', background: 'white', borderRadius: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: '1px solid #e2e8f0', position: 'relative' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '1200px', position: 'relative' }}>
            <thead style={{ position: 'sticky', top: 0, zIndex: 10 }}>
              <tr style={{ background: '#800020' }}>
                <th style={{ padding: '16px', textAlign: 'center', fontWeight: '600', fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.5px', color: 'white', borderRight: '1px solid rgba(255,255,255,0.2)', width: '50px', background: '#800020', position: 'sticky', top: 0, zIndex: 11 }}>##</th>
                {[
                  { label: '👤 ARTISANS', width: '140px' },
                  { label: '⚙️ TODAY’S WORK' },
                  { label: '📋 NEXT PLANNED' },
                  { label: '📦 PREPARATION' },
                  { label: '👥 INCHARGE', width: '160px' },
                  { label: '🔧 PIPE LINE' },
                ].map((col, i) => (
                  <th key={i} style={{ padding: '16px', textAlign: 'left', fontWeight: '600', fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.5px', color: 'white', borderRight: '1px solid rgba(255,255,255,0.2)', ...(col.width ? { width: col.width } : {}), background: '#800020', position: 'sticky', top: 0, zIndex: 11 }}>
                    {col.label}
                  </th>
                ))}
                {canEdit && <th style={{ padding: '16px', textAlign: 'center', fontWeight: '600', fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.5px', color: 'white', width: '80px', background: '#800020', position: 'sticky', top: 0, zIndex: 11 }}>ACTIONS</th>}
              </tr>
            </thead>
            <tbody>
              {filteredData.map((row, index) => (
                <tr key={row.id}>
                  <td style={{ padding: '14px 16px', borderBottom: '1px solid #e2e8f0', borderRight: '1px solid #e2e8f0', background: columnColors.rowNumber.bg, textAlign: 'center', fontWeight: '500', color: '#475569' }}>
                    {index + 1}
                  </td>

                  <td
                    style={{ padding: '14px 16px', borderBottom: '1px solid #e2e8f0', borderRight: '1px solid #e2e8f0', background: 'white', cursor: 'grab' }}
                    draggable="true"
                    onDragStart={e => handleDragStart(e, 'row', row.id)}
                    onDragOver={handleDragOver}
                    onDrop={e => handleDropOnCarpenter(e, row.id)}
                    onDragEnter={e => { e.preventDefault(); setDragOverRowId(row.id); setDragOverField('carpenter'); }}
                    onDragLeave={handleDragLeave}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ fontSize: '16px', color: '#94a3b8', userSelect: 'none' }}>⠿</span>
                      {editingCarpenter?.rowId === row.id ? (
                        <div style={{ display: 'flex', gap: '6px', flex: 1 }}>
                          <input type="text" value={editCarpenterValue} onChange={e => setEditCarpenterValue(e.target.value)} onKeyPress={e => e.key === 'Enter' && saveEditCarpenter()} autoFocus style={{ flex: 1, padding: '6px 8px', borderRadius: '8px', border: '1px solid #800020', outline: 'none', fontSize: '13px' }} />
                          <button onClick={saveEditCarpenter} style={{ background: '#10b981', color: 'white', border: 'none', padding: '4px 10px', borderRadius: '6px', cursor: 'pointer', fontSize: '11px' }}>Save</button>
                          <button onClick={() => { setEditingCarpenter(null); setEditCarpenterValue(''); }} style={{ background: '#94a3b8', color: 'white', border: 'none', padding: '4px 10px', borderRadius: '6px', cursor: 'pointer', fontSize: '11px' }}>Cancel</button>
                        </div>
                      ) : (
                        <div style={{ cursor: 'pointer', fontWeight: '500', display: 'flex', alignItems: 'center', justifyContent: 'space-between', color: '#1e293b', flex: 1 }} onClick={() => startEditCarpenter(row.id, row.carpenter)}>
                          <span>{row.carpenter}</span>
                          <span style={{ fontSize: '12px', color: '#94a3b8' }}>✏️</span>
                        </div>
                      )}
                    </div>
                  </td>

                  {['currentWork', 'nextPlanned', 'preparation'].map(field => (
                    <td
                      key={field}
                      style={{ padding: '14px 16px', verticalAlign: 'top', borderBottom: '1px solid #e2e8f0', borderRight: '1px solid #e2e8f0', background: dragOverRowId === row.id && dragOverField === field ? '#e6f0ff' : columnColors[field].bg, transition: 'background 0.15s' }}
                      onDragOver={handleDragOver}
                      onDrop={e => handleDropOnColumn(e, row.id, field)}
                      onDragEnter={e => handleDragEnter(e, row.id, field)}
                      onDragLeave={handleDragLeave}
                    >
                      {renderMultiItemCell(row.id, row[field], field, `No ${field.replace(/([A-Z])/g, ' $1').toLowerCase()}`, columnColors[field].border)}
                    </td>
                  ))}

                  <td style={{ padding: '14px 16px', verticalAlign: 'top', borderBottom: '1px solid #e2e8f0', borderRight: '1px solid #e2e8f0', background: columnColors.incharge.bg }}>
                    {renderInchargeCell(row.id, row.incharge)}
                  </td>

                  <td
                    style={{ padding: '14px 16px', verticalAlign: 'top', borderBottom: '1px solid #e2e8f0', borderRight: '1px solid #e2e8f0', background: dragOverRowId === row.id && dragOverField === 'pipeLine' ? '#e6f0ff' : columnColors.pipeLine.bg, transition: 'background 0.15s' }}
                    onDragOver={handleDragOver}
                    onDrop={e => handleDropOnColumn(e, row.id, 'pipeLine')}
                    onDragEnter={e => handleDragEnter(e, row.id, 'pipeLine')}
                    onDragLeave={handleDragLeave}
                  >
                    {renderMultiItemCell(row.id, row.pipeLine, 'pipeLine', 'No pipeline tasks', columnColors.pipeLine.border)}
                  </td>

                  {canEdit && (
                    <td style={{ padding: '14px 16px', textAlign: 'center', borderBottom: '1px solid #e2e8f0', background: 'white' }}>
                      <button
                        onClick={() => handleDeleteRow(row.id, row.carpenter)}
                        style={{ background: '#fef2f2', color: '#dc2626', border: '1px solid #fee2e2', padding: '6px 12px', borderRadius: '8px', cursor: 'pointer', fontSize: '11px', transition: 'all 0.2s ease' }}
                        onMouseEnter={e => { e.currentTarget.style.background = '#dc2626'; e.currentTarget.style.color = 'white'; }}
                        onMouseLeave={e => { e.currentTarget.style.background = '#fef2f2'; e.currentTarget.style.color = '#dc2626'; }}
                      >🗑️ Delete</button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Legend */}
      {plannerData.length > 0 && (
        <div style={{ marginTop: '20px', padding: '14px 20px', background: 'white', borderRadius: '12px', display: 'flex', gap: '20px', flexWrap: 'wrap', justifyContent: 'center', border: '1px solid #e2e8f0' }}>
          {Object.entries(columnColors).filter(([k]) => k !== 'actions').map(([key, val]) => (
            <div key={key} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <div style={{ width: '16px', height: '16px', background: val.bg, border: `1px solid ${val.border}`, borderRadius: '4px' }} />
              <span style={{ fontSize: '12px', color: '#475569' }}>
                {key === 'rowNumber' ? 'Row #' :
                 key === 'carpenter' ? 'ARTISANS' :
                 key === 'currentWork' ? 'TODAY’S WORK' :
                 key.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase())}
              </span>
            </div>
          ))}
          <span style={{ fontSize: '12px', color: '#475569' }}>⠿ Drag rows | ✏️ Edit | ✕ Remove | 📄 PDF</span>
          <span style={{ fontSize: '12px', color: '#800020', fontWeight: '500' }}>🔄 Auto‑move based on dates | ⏳ days left/overdue</span>
        </div>
      )}
    </>
  );

  // ========== RETURN (with updated tabs) ==========
  return (
    <div style={{ padding: '24px', background: '#f8fafc', minHeight: '100vh' }}>
      <div style={{ display: 'flex', gap: '0', background: 'white', padding: '0 24px', borderBottom: '2px solid #800020', borderRadius: '12px 12px 0 0', marginBottom: '24px' }}>
        {[
          { key: 'planner', label: '📋 Project Planner' },
          { key: 'artisans', label: '👷 Artisans View' },  // NEW TAB
          { key: 'clients', label: '👥 Client View' },
        ].map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            style={{ padding: '12px 24px', background: activeTab === tab.key ? '#800020' : 'transparent', color: activeTab === tab.key ? 'white' : '#94a3b8', border: 'none', cursor: 'pointer', fontWeight: '600', fontSize: '14px', transition: 'all 0.2s', borderBottom: activeTab === tab.key ? '3px solid #fbbf24' : '3px solid transparent', borderRadius: '8px 8px 0 0' }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Conditional Rendering */}
      {activeTab === 'planner' && renderPlanner()}
      {activeTab === 'artisans' && <ArtisansView plannerData={plannerData} clientsList={clientsList} />}
      {activeTab === 'clients' && <ClientView currentUser={currentUser} userRole={userRole} />}
    </div>
  );
};

export default ProjectPlanner;